import type * as Y from 'yjs';

import { type LazyValue, normalizeLazyValue } from './lazy-value';
import {
  type BasicYArray,
  type DisposableElement,
  type WrappedYType,
  isDisposableElement,
  isWrappedYType
} from './types';
import { registerIntegrateHandler, unregisterIntegrateHandler } from './utils';

/**
 * A wrapper for BasicYArray that maps its values through the given mapper function and
 * ignores/deletes any elements for which the mapper function returns undefined.
 */
export class SynchronizedArray<T>
  implements BasicYArray<T>, DisposableElement, WrappedYType
{
  private yarray: LazyValue<BasicYArray<unknown>>;
  private values: T[] = [];

  constructor(
    yarray: BasicYArray<unknown> | LazyValue<BasicYArray<unknown>>,
    private mapper: (val: unknown) => T | undefined
  ) {
    this.yarray = normalizeLazyValue(yarray);
    this.updateValues();

    this.yarray.observeDeep(this.onChange);
    this.yarray.on('afterInitialize', this.onChange);

    const unwrapped = this.unwrap();
    if (unwrapped) {
      registerIntegrateHandler(unwrapped, this.onChange);
    }
  }

  get doc(): Y.Doc | null {
    return this.yarray.value?.doc ?? null;
  }

  slice(start?: number | undefined, end?: number | undefined): T[] {
    return this.values.slice(start, end);
  }

  get(idx: number): T {
    return this.values[idx];
  }

  get length(): number {
    return this.values.length;
  }

  toArray(): T[] {
    return [...this.values];
  }

  insert(idx: number, values: T[]): void {
    this.removeInvalidValues();

    this.values = [...this.values.slice(0, idx), ...values, ...this.values.slice(idx)];

    this.yarray.valueRequired.insert(
      idx,
      values.map((v) => this.unmapValue(v))
    );
  }

  push(values: T[]): void {
    this.removeInvalidValues();

    this.values.push(...values);
    this.yarray.valueRequired.push(values.map((v) => this.unmapValue(v)));
  }

  unshift(values: T[]): void {
    this.removeInvalidValues();

    this.values.unshift(...values);
    this.yarray.valueRequired.unshift(values.map((v) => this.unmapValue(v)));
  }

  delete(idx: number): void {
    this.removeInvalidValues();

    this.values.splice(idx, 1);
    this.yarray.valueRequired.delete(idx);
  }

  forEach(f: (arg0: T, arg1: number, arg2: BasicYArray<T>) => void): void {
    this.toArray().forEach((el, i) => {
      f(el, i, this);
    });
  }

  observe(f: (arg0: Y.YArrayEvent<any>, arg1: Y.Transaction) => void): void {
    this.yarray.observe(f);
  }

  observeDeep(f: (arg0: Y.YEvent<any>[], arg1: Y.Transaction) => void): void {
    this.yarray.observeDeep(f);
  }

  unobserve(f: (arg0: Y.YArrayEvent<any>, arg1: Y.Transaction) => void): void {
    this.yarray.unobserve(f);
  }

  unobserveDeep(f: (arg0: Y.YEvent<any>[], arg1: Y.Transaction) => void): void {
    this.yarray.unobserveDeep(f);
  }

  /**
   * Stores a copy of the validated values in this.values.
   */
  private updateValues() {
    for (const value of this.values) {
      if (isDisposableElement(value)) {
        value.dispose();
      }
    }

    this.values =
      (
        (this.yarray.value as any)?.['_prelimContent'] ?? this.yarray.value?.toArray()
      )?.flatMap((v: unknown) => {
        const mapped = this.mapper(v);
        return mapped !== undefined ? [mapped] : [];
      }) ?? [];
  }

  /**
   * Removes invalid values from the underlying YArray.
   */
  private removeInvalidValues() {
    for (let i = this.yarray.valueRequired.length - 1; i >= 0; i--) {
      if (this.mapper(this.yarray.valueRequired.get(i)) === undefined) {
        this.yarray.valueRequired.delete(i);
      }
    }
  }

  private unmapValue(val: T): unknown {
    if (isWrappedYType(val)) {
      return val.unwrap();
    } else {
      return val;
    }
  }

  private onChange = () => {
    this.updateValues();
  };

  unwrap(): Y.AbstractType<any> | undefined {
    return this.yarray.unwrap();
  }

  dispose(): void {
    this.yarray.unobserveDeep(this.onChange);
    this.yarray.off('afterInitialize', this.onChange);
    const unwrapped = this.unwrap();
    if (unwrapped) {
      unregisterIntegrateHandler(unwrapped, this.onChange);
    }

    for (const value of this.values) {
      if (isDisposableElement(value)) {
        value.dispose();
      }
    }
  }
}
